import { useCloseUnlessOrgNode } from '../hooks/useCloseWhenMissing'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import useMergedData from '../hooks/useMergedData'
import useOutsideNodes from '../hooks/useOutsideNodes'
import { useOrgRole } from '../hooks/useOrgRole'
import type { FileTarget } from '../lib/webdavClient'
import FileBrowser from './FileBrowser'

// A node's OWN storage — persistent, follows the machine, and belongs to no
// network at all.
//
// ⚠ That is why it is addressed by node id with no network in the path: a node
// can join several networks, so "the node's network" is not a thing this area
// has. It is also why a node name would not do — node names are unique per
// network, and this area sits outside all of them.
//
// ⚠ Reaching it needs Owner or Admin in the org that owns the node — a higher
// bar than the node's own files elsewhere in the app, because a WebDAV write
// here has no agent-side veto in front of it: a file planted in this area is
// one the node may later read, trust, or execute. A Member gets a 403 that says
// so; someone outside the org gets a 404, because the node's existence is not
// theirs to learn.
export default function NodeFiles() {
  const { nodeId = '' } = useParams<{ nodeId: string }>()
  // ⚠ An id in the path that is not an object of the active org sends the reader home (app#475).
  useCloseUnlessOrgNode(nodeId)
  const { nodes } = useMergedData()
  // A node of the active org in another org's network, or in no network, is not in
  // `useMergedData` (app#388). Its storage is still its tenant's.
  const outside = useOutsideNodes()
  const node = nodes.find(n => n.id === nodeId) ?? outside.find(o => o.node.id === nodeId)?.node
  // The role in the node's TENANT, `owner_org_id` (G-1, app#385): the storage area is the
  // tenant's, and `AuthorizeNodeStorage` asks that org, never the org of the node's network.
  const { can } = useOrgRole(node ? (node.ownerOrgId ?? null) : undefined)
  const allowed = can('node.storage')

  const target = useMemo<FileTarget>(() => ({ tree: 'nodes', id: nodeId }), [nodeId])

  return (
    <FileBrowser
      target={target}
      // ⚠ THE COLD-ENTRY HALF OF THE ROLE GATE. Hiding `StorageSection` on the
      // node page removes the LINK; this route is still reachable by a pasted
      // URL, a bookmark, history, or Back after a role change — and `found` is
      // what lets `FileBrowser` list, so a Member arriving that way would fire
      // exactly the guaranteed-403 PROPFIND the section gate exists to prevent
      // and see it as a raw transport error. Refusing here says why instead.
      //
      // ⚠ Ordered node-first: `!node` must stay "Node not found", because a
      // Member outside the org is told nothing about the node's existence. The
      // role message is reachable only once the node has RESOLVED, and by then
      // `role` is settled — controller.loadState awaits useOrganizations before
      // nodes — so it cannot flash on a hard refresh.
      found={!!node && allowed}
      basePath={`/nodes/${nodeId}/files`}
      // The node LIST. This is the COLD-ENTRY path — a pasted or shared URL, where there is no
      // navigation state to carry a better target.
      defaultBackTo="/nodes"
      defaultBackLabel="Nodes"
      notFoundBackTo="/nodes"
      notFoundMessage={
        node
          ? "Node storage requires owner or admin in the node's organization."
          : 'Node not found.'
      }
    />
  )
}
